/**
 * Клиент Cabinet API (docs/08 §8.2).
 *
 * Сессия — в cookie, поэтому запросы идут с `credentials: 'same-origin'`, а
 * изменяющие — с заголовком `X-Requested-With`: без него сервер отвечает 403
 * (это вторая половина защиты от CSRF).
 */

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Сессия кончилась или её не было — SPA уводит на вход. */
  get needsLogin(): boolean {
    return this.status === 401
  }
}

type Query = Record<string, string | number | boolean | undefined>

async function request<T>(
  method: string,
  path: string,
  options: { body?: unknown; query?: Query } = {},
): Promise<T> {
  const url = new URL(path, location.origin)
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value))
  }

  const res = await fetch(url.toString(), {
    method,
    credentials: 'same-origin',
    headers: {
      ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(method === 'GET' ? {} : { 'X-Requested-With': 'XMLHttpRequest' }),
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  })

  if (res.status === 204) return undefined as T
  const text = await res.text()
  const parsed: unknown = text ? JSON.parse(text) : null

  if (!res.ok) {
    const error = (parsed as { error?: { code?: string; message?: string } } | null)?.error
    throw new ApiError(
      error?.code ?? `http_${res.status}`,
      res.status,
      error?.message ?? 'Запрос не выполнен',
    )
  }
  return parsed as T
}

/**
 * Загрузка файла сырым телом (этап 6б): имя — в query, тип определяет сервер.
 * Отдельно от `request`, потому что тело здесь не JSON.
 */
async function upload<T>(path: string, file: File): Promise<T> {
  const url = new URL(path, location.origin)
  url.searchParams.set('name', file.name)
  const res = await fetch(url.toString(), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/octet-stream', 'X-Requested-With': 'XMLHttpRequest' },
    body: file,
  })
  const text = await res.text()
  const parsed: unknown = text ? JSON.parse(text) : null
  if (!res.ok) {
    const error = (parsed as { error?: { code?: string; message?: string } } | null)?.error
    throw new ApiError(
      error?.code ?? `http_${res.status}`,
      res.status,
      error?.message ?? 'Файл не загрузился',
    )
  }
  return parsed as T
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, query ? { query } : {}),
  upload,
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body }),
  del: <T>(path: string) => request<T>('DELETE', path),
}

/**
 * Ссылка на выгрузку. Файл скачивает сам браузер обычной навигацией: cookie
 * уйдёт, а заголовок `X-Requested-With` для GET не нужен.
 */
export function downloadUrl(path: string, query: Query = {}): string {
  const url = new URL(path, location.origin)
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value))
  }
  return url.toString()
}
